import axios from 'axios';

import {
    LumeFxAmendOrderRequest,
    LumeFxOrderType,
    LumeFxSide,
    LumeFxTimeInForce,
    type LumeFxCancelAllOrdersResponse,
    type LumeFxCancelOrderResponse,
    type LumeFxOrderBookQueryParams,
    type LumeFxOrderBookSnapshotResponse,
    type LumeFxOrderQueryParams,
    type LumeFxOrdersQueryParams,
    type LumeFxOrdersResponse,
    type LumeFxOrderStatusResponse,
    type LumeFxPlaceOrderRequest,
    type LumeFxPlaceOrderResponse,
    type LumeFxRestHeaders,
    type LumeFxWebSocketLogon,
    type LumeFxWebSocketOrderBookRequest
} from '../types/lumeFx';

export interface LumeFxRestClientConfig {
    baseUrl: string;
    authorization?: string;
}

export function createLumeFxRestClient({ baseUrl, authorization }: LumeFxRestClientConfig) {
    const headers: LumeFxRestHeaders = {
        'Content-Type': 'application/json',
        authorization: `Bearer ${authorization}`
    };

    const client = axios.create({
        baseURL: baseUrl.replace(/\/+$/, ''),
        headers: { ...headers }
    });

    return {
        placeOrder: async (order: LumeFxPlaceOrderRequest) => {
            const payload = {
                ...order,
                TimeInForce: order.TimeInForce,
                OrdType: LumeFxOrderType[order.OrdType],
                Side: order.Side
            };

            return (await client.post<LumeFxPlaceOrderResponse>('/api/v1/order', payload)).data;
        },

        amendOrder: async (order: LumeFxAmendOrderRequest) => {
            const payload = {
                ...order,
                TimeInForce: LumeFxTimeInForce[order.TimeInForce],
                OrdType: LumeFxOrderType[order.OrdType],
                Side: LumeFxSide[order.Side]
            };

            return (await client.patch<LumeFxPlaceOrderResponse>(`/api/v1/order?clordid=${order.OrigClOrdID}`, payload))
                .data;
        },

        cancelOrder: async ({ clordid }: LumeFxOrderQueryParams) => {
            try {
                return (await client.delete<LumeFxCancelOrderResponse>('/api/v1/order', { params: { clordid } })).data;
            } catch (error) {
                console.error('Error cancelling order:', error);
            }
        },

        getOrderStatus: async ({ clordid }: LumeFxOrderQueryParams) =>
            (await client.get<LumeFxOrderStatusResponse>('/api/v1/order', { params: { clordid } })).data,

        getOrders: async ({ ordStatus }: LumeFxOrdersQueryParams) =>
            (await client.get<LumeFxOrdersResponse>('/api/v1/orders', { params: { ordStatus } })).data,

        cancelAllOrders: async () => (await client.delete<LumeFxCancelAllOrdersResponse>('/api/v1/orders')).data,

        getOrderBook: async ({ symbol }: LumeFxOrderBookQueryParams) =>
            (await client.get<LumeFxOrderBookSnapshotResponse>('/api/v1/orderbook', { params: { symbol } })).data
    };
}

export function connectLumeFxWebSocket(url: string, authentication: string): WebSocket {
    const socket = new WebSocket(url);
    const logon: LumeFxWebSocketLogon = { msg_type: 'logon', authentication };

    socket.addEventListener('open', () => {
        socket.send(JSON.stringify(logon));
    });

    return socket;
}

export function sendLumeFxOrderBookRequest(socket: WebSocket, request: LumeFxWebSocketOrderBookRequest): void {
    if (socket.readyState !== WebSocket.OPEN) {
        throw new Error('Cannot send a LumeFX WebSocket request while the connection is not open.');
    }

    socket.send(JSON.stringify(request));
}

export function subscribeLumeFxOrderBook(socket: WebSocket, symbol: string, depth?: number): void {
    sendLumeFxOrderBookRequest(socket, {
        msg_type: 'orderbook_request',
        action: 'subscribe',
        symbol,
        ...(depth === undefined ? {} : { depth })
    });
}

export function unsubscribeLumeFxOrderBook(socket: WebSocket, symbol: string): void {
    sendLumeFxOrderBookRequest(socket, {
        msg_type: 'orderbook_request',
        action: 'unsubscribe',
        symbol
    });
}
